import { Slot } from '@radix-ui/react-slot';
import * as React from 'react';

import { cn } from '../../../lib/utils';

/**
 * InlineDividedList
 *
 * This component provides a flexible way to render a horizontal, inline list of items
 * with a customizable separator between each item. It is useful for displaying lists
 * such as metadata, tags, or navigation links in a compact, visually separated format.
 *
 * - Renders its children in a single line (with wrapping if needed), separated by a
 *   specified separator (default: '|').
 *
 * Example render: {component} | {component} | {component}
 *
 * Example usage:
 *   <InlineDividedList>
 *     <InlineDividedList.Item>Item 1</InlineDividedList.Item>
 *     <InlineDividedList.Item>Item 2</InlineDividedList.Item>
 *     <InlineDividedList.Item>Item 3</InlineDividedList.Item>
 *   </InlineDividedList>
 */
type InlineDividedListProps = React.ComponentPropsWithoutRef<'div'> & {
  asChild?: boolean;
  separator?: React.ReactNode;
};

const InlineDividedListRoot = React.forwardRef<
  HTMLDivElement,
  InlineDividedListProps
>(function InlineDividedList(
  { className, children, asChild = false, separator = '|', ...props },
  ref,
) {
  const Comp = asChild ? Slot : 'div';

  const childArray = React.Children.toArray(children);

  return (
    <Comp
      ref={ref}
      className={cn(
        'inline-flex flex-nowrap items-center whitespace-nowrap',
        className,
      )}
      {...props}
    >
      {childArray.map((child, index) => {
        const isLast = index === childArray.length - 1;
        const childKey =
          React.isValidElement(child) && child.key != null ? child.key : index;
        return (
          <React.Fragment key={childKey}>
            {child}
            {!isLast ? (
              // Separators keep their width when the list is squeezed —
              // truncation belongs to the items, never to the dividers.
              <span aria-hidden="true" className={cn('mx-2 shrink-0')}>
                {separator}
              </span>
            ) : null}
          </React.Fragment>
        );
      })}
    </Comp>
  );
});

/**
 * InlineDividedList.Item
 *
 * Represents a single item within the InlineDividedList. Supports the `asChild` prop
 * for advanced composition, and accepts all standard span props.
 */
type InlineDividedListItemProps = React.ComponentPropsWithoutRef<'span'> & {
  asChild?: boolean;
};

const InlineDividedListItem = React.forwardRef<
  HTMLSpanElement,
  InlineDividedListItemProps
>(function InlineDividedListItem(
  { className, asChild = false, ...props },
  ref,
) {
  const Comp = asChild ? Slot : 'span';
  return <Comp ref={ref} className={cn(className)} {...props} />;
});

InlineDividedListItem.displayName = 'InlineDividedList.Item';
InlineDividedListRoot.displayName = 'InlineDividedList';

export const InlineDividedList = Object.assign(InlineDividedListRoot, {
  Item: InlineDividedListItem,
});
